import join from "lodash/join";
import slice from "lodash/slice";
import map from "lodash/map";

import { PickUpVariant, Stop, Route, Trip } from "types";
import { getPassenger } from "globals/utils/passenger";

// Helper to format stop location with flight info
export const formatStopLocation = (stop: Stop): string =>
  stop.trackedFlight
    ? `${stop.airport.airportName} - ${stop.trackedFlight.flightNumber}`
    : stop.location;

// Helper to get pickup variant display text
export const getPickupVariantText = (
  pickUpVariant: PickUpVariant | null | undefined
): string | null => {
  if (pickUpVariant === PickUpVariant.MeetGreet) return "Meet & Greet";
  if (pickUpVariant === PickUpVariant.Curbside) return "Curbside";
  return null;
};

// Helper to format luggage information
export const formatLuggageInfo = (route: Route): string => {
  const carryOnLuggage = route?.carryOnLuggage || 0;
  const checkedLuggage = route?.checkedLuggage || 0;
  const oversizeLuggage = route?.oversizeLuggage || 0;

  const luggageItems = [];
  if (carryOnLuggage > 0) luggageItems.push(`${carryOnLuggage} Carry On`);
  if (checkedLuggage > 0) luggageItems.push(`${checkedLuggage} Checked`);
  if (oversizeLuggage > 0) luggageItems.push(`${oversizeLuggage} Oversize`);

  return luggageItems.length > 0 ? luggageItems.join(", ") : "None";
};

// Helper to format stop note with proper prefix
export const formatStopNote = (
  note: string | null | undefined,
  prefix: string
): string => {
  return note ? `\n${prefix}: ${note}` : "";
};

// Helper to format passenger display based on farmee status
export const formatPassengerDisplay = (
  trip: Trip,
  isFarmee: boolean
): string => {
  if (isFarmee) {
    const farmeePassenger = getPassenger(
      trip.contact,
      trip.tempPassenger,
      true
    );
    return farmeePassenger?.name || "";
  }

  const passenger = trip.tempPassenger?.name
    ? `${trip.tempPassenger.name} Ph: ${
        trip.tempPassenger.mobilePhone || "N/A"
      }`
    : `${trip.contact.firstName} ${trip.contact.lastName} Ph: ${
        trip.contact.mobilePhone || "N/A"
      }`;

  return passenger;
};

// Helper to format inner stops with notes
export const formatInnerStops = (stops: Stop[]): string => {
  const innerStopsArray = slice(stops, 1, -1);

  return join(
    map(innerStopsArray, (stop, index) => {
      const stopLocation = formatStopLocation(stop);
      const stopNote = stop.note
        ? `\nStop ${index + 1} Notes: ${stop.note}`
        : "";
      return `\n[ST${index + 1}]: ${stopLocation}${stopNote}`;
    }),
    ""
  );
};
